/**
 * MerchantScreeningAgGridHook
 *
 * AG Grid hook for the Merchant Screening LiveView (/admin/merchants/screening).
 *
 * LiveView events received:
 *  "update_merchant_screening_data" – { rows: [...] } – pushed after filter change.
 */

const MerchantScreeningAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");

    const badge = (value, colours) => {
      if (!value) return "";
      const bg = colours[value] || "#9CA3AF";
      return `<span style="background:${bg};color:#fff;padding:2px 8px;border-radius:9999px;
        font-size:11px;font-weight:600;white-space:nowrap;">${value}</span>`;
    };

    const riskColours = {
      low: "#16A34A",
      medium: "#D97706",
      high: "#DC2626",
      incomplete: "#9CA3AF",
      terminated: "#1F2937",
    };

    const caseStatusColours = {
      completed: "#16A34A",
      approved: "#16A34A",
      rejected: "#DC2626",
      escalated: "#DC2626",
      in_review: "#2563EB",
      info_pending: "#D97706",
      pending: "#9CA3AF",
    };

    const priorityColours = {
      critical: "#DC2626",
      high: "#D97706",
      medium: "#2563EB",
      low: "#9CA3AF",
    };

    const subScoresCell = (params) => {
      if (!params.data) return "";
      const d = params.data;
      const parts = [];
      if (d.mcc_score != null) parts.push(`MCC ${d.mcc_score}`);
      if (d.jurisdiction_score != null) parts.push(`Jur ${d.jurisdiction_score}`);
      if (d.ownership_score != null) parts.push(`Own ${d.ownership_score}`);
      if (d.pep_score != null) parts.push(`PEP ${d.pep_score}`);
      if (d.adverse_media_score != null) parts.push(`AM ${d.adverse_media_score}`);
      return parts.length ? parts.join(" · ") : "—";
    };

    const caseStatusCell = (params) => {
      if (!params.data) return "";
      const html = badge(params.value, caseStatusColours);
      const overdue = params.data.is_overdue
        ? `<div style="color:#DC2626;font-size:10px;margin-top:2px;">overdue</div>`
        : "";
      return `<div>${html}${overdue}</div>`;
    };

    const actionCell = (params) => {
      if (!params.data || !params.data.mms_url) return "";
      const wrap = document.createElement("a");
      wrap.href = params.data.mms_url;
      wrap.target = "_blank";
      wrap.rel = "noopener noreferrer";
      wrap.textContent = "View in MMS ↗";
      wrap.style.cssText = "color:#4f46e5;font-size:11px;text-decoration:underline;";
      return wrap;
    };

    const columnDefs = [
      { headerName: "Merchant", field: "merchant_name", width: 180, pinned: "left" },
      { headerName: "Email", field: "merchant_email", width: 200 },
      {
        headerName: "Risk Category",
        field: "risk_category",
        width: 140,
        cellRenderer: (p) => (p.value ? badge(p.value, riskColours) : `<span style="color:#9CA3AF;font-size:11px;">not scored</span>`),
      },
      { headerName: "Sub-scores", colId: "sub_scores", width: 220, sortable: false, filter: false, cellRenderer: subScoresCell },
      { headerName: "Workflow", field: "workflow_status", width: 140 },
      {
        headerName: "Case Status",
        field: "case_status",
        width: 140,
        cellRenderer: caseStatusCell,
      },
      {
        headerName: "Priority",
        field: "priority",
        width: 110,
        cellRenderer: (p) => badge(p.value, priorityColours),
      },
      { headerName: "SLA Due", field: "sla_due_date", width: 160 },
      {
        headerName: "Action",
        colId: "action",
        width: 130,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: actionCell,
      },
    ];

    const gridOptions = {
      columnDefs,
      rowData: rows,
      pagination: true,
      paginationPageSize: 25,
      paginationPageSizeSelector: [25, 50, 100, 250],
      domLayout: "autoHeight",
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        minWidth: 90,
      },
      animateRows: true,
      overlayNoRowsTemplate: `<span style="padding:20px;color:#6B7280;">No screening cases match.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
    } else {
      console.error("MerchantScreeningAgGridHook: agGrid not found on window");
    }

    this.handleEvent("update_merchant_screening_data", ({ rows: newRows }) => {
      if (this.gridApi) {
        this.gridApi.setGridOption("rowData", newRows || []);
      }
    });
  },

  updated() {
    if (this.gridApi && this.el.dataset.rows) {
      try {
        const rows = JSON.parse(this.el.dataset.rows);
        this.gridApi.setGridOption("rowData", rows);
      } catch (e) {
        console.warn("MerchantScreeningAgGridHook updated(): failed to parse rows", e);
      }
    }
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default MerchantScreeningAgGridHook;
